import * as React from 'react'
import { closeTourModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import { WidgetToursController } from '../../controllers'
import { CurrentUser, DisplayableError, WidgetTour } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import CheckboxInput from '../Form/CheckboxInput'
import Notification from '../../utilities/Notification'

interface IStateToProps {
  currentUser: CurrentUser
  widgetId: string
  tour?: WidgetTour
  onSubmit?: (tour: WidgetTour) => void
}

interface IDispatchToProps {
  close: typeof closeTourModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  tour: WidgetTour
  errors: DisplayableError[]
  isSubmitting: boolean
}

const TourModal = (props: IProps) => {
  const { t } = useTranslation()
  const { widgetId } = props
  const [state, setState] = React.useState<IState>({
    tour: props.tour || { name: '', enabled: true },
    errors: [],
    isSubmitting: false,
  })
  const { tour, errors, isSubmitting } = state

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, tour: { ...tour, name: e.currentTarget.value } })
  }

  const onEnabledChange = (checked: boolean) => {
    setState({ ...state, tour: { ...tour, enabled: checked } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    if (!tour?.name) return

    setState({ ...state, isSubmitting: true })

    try {
      const action = tour.id
        ? WidgetToursController.update(widgetId, tour)
        : WidgetToursController.create(widgetId, tour)

      const response = await action

      if (response.errors) {
        setState({ ...state, errors: response.errors, isSubmitting: false })
      } else {
        Notification.notifySuccess(
          tour.id
            ? t('TourModal::Tour successfully updated')
            : t('TourModal::Tour successfully created')
        )
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }
    } catch (ex) {
      console.error(ex)
      setState({ ...state, isSubmitting: false })
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={tour?.id ? t('TourModal::Edit tour') : t('TourModal::New tour')}
        onCloseClick={onCloseClick}
      />

      <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('TourModal::Name')} <span>*</span></label>
                  <input
                    type='text'
                    value={tour?.name || ''}
                    onChange={onNameChange}
                    placeholder={t('TourModal::Enter a name')}
                  />
                </div>
              </div>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('TourModal::Enabled')}</label>
                  <CheckboxInput
                    checked={tour?.enabled}
                    onChange={onEnabledChange}
                    label={t('TourModal::Enabled')}
                  />
                </div>
              </div>
            </div>
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={tour?.id ? t('TourModal::Update') : t('TourModal::Create')}
              onClick={onFormSubmit}
              disabled={!tour?.name || isSubmitting}
            />
          </div>
        </div>
      </ModalMiddle>
    </ModalWindow>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: { currentUser },
    modals: {
      tourModal: {
        widgetId,
        tour,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser,
    widgetId,
    tour,
    onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeTourModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TourModal)
